export const MAX_CHART_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_CHART_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'] as const;
export const MIN_CHART_WIDTH = 400;
export const MIN_CHART_HEIGHT = 250;

export type DimensionProbe = (file: File) => Promise<{ width: number; height: number }>;

// Default probe loads the file via an HTMLImageElement to read its natural size.
const defaultProbe: DimensionProbe = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });

/**
 * Validate a chart file: type (PNG/JPG/SVG), size (<=5MB), and — for raster
 * images — minimum dimensions (>=400x250). SVG is vector, so its dimensions
 * are not probed. Returns an error message, or null when valid.
 */
export async function validateChartFile(
  file: File,
  probe: DimensionProbe = defaultProbe
): Promise<string | null> {
  if (!ACCEPTED_CHART_TYPES.includes(file.type as (typeof ACCEPTED_CHART_TYPES)[number])) {
    return 'Chart must be a PNG, JPG or SVG image';
  }
  if (file.size > MAX_CHART_BYTES) {
    return 'Chart image must be 5MB or smaller';
  }
  if (file.type === 'image/svg+xml') {
    return null; // vector — no pixel dimensions to check
  }
  try {
    const { width, height } = await probe(file);
    if (width < MIN_CHART_WIDTH || height < MIN_CHART_HEIGHT) {
      return `Chart image is too small - it must be at least ${MIN_CHART_WIDTH}×${MIN_CHART_HEIGHT}px`;
    }
  } catch {
    return 'Could not read the chart image dimensions';
  }
  return null;
}
